import { ElementType } from 'react';

import type { QuoteCalculatorConfig } from '~/core/broker-portal/components/QuoteCalculator/types/QuoteCalculatorConfig';
import type { DealsListConfig } from '../components/deals';
import {
  EnvelopeStatus,
  FeatureKey,
  VerificationTransactionStatus,
} from '../constants';
import { Entity, HasId, Nullable, Option } from './utility';
import { AnyDocument } from './workflows';

export type Workbook = {
  id: string;
  content: string;
};

export type Category = HasId & {
  childCategories?: Category[];
  identifier: string;
  meta: Entity;
  parent: string;
  title: string;
  type: 'category';
  value: string;
};

export type Organisation = {
  id: string;
  name: string;
  assignedBdm?: any;
  highPriority?: any;
  role: string[];
  permissions: string[];
  // Extended attributes
  [key: string]: string | number | string[];
};

export type User = {
  email: string;
  id: string;
  name: string;
  twoFactorRequired?: boolean;
  twoFactorEnabled: boolean;
  twoFactorMethod: string;
  createdAt: string;
  role: string[];
  permissions: string[];
  organisations?: Organisation[];

  // @TODO: need to refactor and add support for dynamic user fields to this type
  phoneMeta?: any;
  phone?: string;
  position?: string;
  faxNumber?: string;
  documentFilter?: string;
  isBdm?: string;
  deletedAt?: string;
};

export type Brokerage = Organisation & {
  abn: string;
  acn: string;
  webAddress: string;
};

export type BrokerPortalUser = User & {
  position: string;
  brokerages?: Brokerage[];

  // @TODO: remove or replace
  hasManagerAccess: boolean;
  firstName: string;
  lastName: string;
  accreditationStatus: boolean;
  bdm?: BrokerPortalUser;
  address?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
};

type TemplateField = {
  id: string;
  type: 'template-field';
  label: string;
  client_id: string;
  field_type: string;
  is_required: boolean;
  is_hidden: boolean;
};

export type AccordionRadioGroup = {
  icon?: ElementType | string;
  label: string;
  options: Option<number | string>[];
};

export type TemplateRole = {
  id: string;
  type: 'template-role';
  title: string;
  fields: TemplateField[];
};

export type Template = {
  id: string;
  type: 'template';
  title: string;
  roles: TemplateRole[];
};

export type TemplateInstance = {
  id: string;
  type: 'template-instance';
  template_id: string;
  sort_order: number;
  template?: Template;
  document?: Document;
};

export type EnvelopeLog = {
  id: string;
  type: 'envelope-log';
  status: string;
  created_at: string;
  updated_at: string;
};

export type Envelope = {
  id: string;
  type: 'envelope';
  name: string;
  service_identifier: string;
  status: EnvelopeStatus;
  subject: string;
  created_by: Nullable<User>;
  sent_by: Nullable<User>;
  sent_at: string;
  latest_log: EnvelopeLog;
  instances?: TemplateInstance[];
  is_voidable: boolean;
  created_at: string;
  updated_at: string;
};

export type RecipientField = {
  id: string;
  field_id: string;
  value: string;
};

export type EnvelopeDraft = {
  id: string;
  type: 'envelope-draft';
  data: string;
  created_at: string;
  updated_at: string;
};

export type Recipient = {
  id: string;
  type: 'recipient';
  email: string;
  name: string;
  fields: RecipientField[];
  logs: EnvelopeLog[];
};

export type ModelOption = {
  value: string;
};

export type Workflow = {
  name: string;
  supportedDocuments: string[];
};

export type Role = {
  id: string;
  name: string;
  readonly: boolean;
  permissions: string[];
  permissionNames: string[];
};

export type Permission = {
  id: string;
  name?: string;
  displayName: string;
};

export type PermissionMatrixUserPermission = {
  name: string;
  enabled: boolean;
  via?: string;
};

export type UserInPermission = {
  id: string;
  name: string;
  roles: string;
  permissions: PermissionMatrixUserPermission[];
};

export type PermissionGroup = {
  id: string;
  name: string;
  permissions: Permission[];
};

type MedicareVerified = {
  medicareCardColour: string;
  medicareCardNumber: string;
  medicareFirstNameOnCard: string;
  medicareLastNameOnCard: string;
  medicareMonthYearOfExpiry: string;
  medicareReferenceNumber: string;
};

type DriverLicenceVerified = {
  licenceCardNumber: string;
  licenceNumber: string;
  nameAsOnDriversLicence: string;
  stateOfIssue: string;
};

type IdVerified = {
  idVerificationTransactionStatus: VerificationTransactionStatus;
};

type IndividualVerification = MedicareVerified &
  DriverLicenceVerified &
  IdVerified;

type BaseIndividual = {
  createdAt: string; // TODO: make date time string
  dateOfBirth: string; // TODO: make date string
  definition: string;
  docsName: string;
  email: string;
  firstName: string;
  fullName: string;
  gender: string; // TODO: make enum
  id: string;
  lastName: string;
  middleName?: string;
  phone: string;
  shortId: string;
  updatedAt: string; // TODO: make date time string
} & IndividualVerification;

export type IndividualGuarantor = BaseIndividual & {
  dateOfAppointment: string; // TODO: make date string
  hasConfirmedDetails: boolean;
  middleName: string;
  guarantor: boolean;
  residencyOverseas: boolean;
  residentialStatus: string; // TODO: make enum
  role: string;
};

type IndividualApplicant = BaseIndividual & {
  citizenship: string; // TODO: make enum
  maritalStatus: string; // TODO: make enum
  numberOfDependants: number;
};

export type Individual = IndividualGuarantor | IndividualApplicant;

export type CategorizedIndividual = Individual & {
  type: string;
};

export type BulkFile = File & {
  isUploading?: boolean;
};

export type TemplateRoleContact = {
  id: string;
  type: 'template-role-contact';
  definition: string;
  name: string;
  email: string;
};

export type Statistic = {
  text: string;
  value: string;
};

export type BDM = {
  id: string;
  name: string;
};

export type Broker = {
  id: string;
  name: string;
  email: string;
  userId?: string;
  bdm?: BDM;
  organisation?: Organisation;
  role: string[];
  twoFactorMethod?: string;
  twoFactorRequired?: boolean;
  address?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  phone?: string;
  deletedAt?: string;
};

export enum ActivityCauserType {
  Agent = 'agent',
  Broker = 'broker',
  System = 'system',
}

export type Activity = {
  id: string;
  description: string;
  causerName: string;
  causerType: ActivityCauserType;
  createdAt: string;
  updatedAt: string;
};

type AbnStatus = 'Active' | 'Cancelled' | 'Inactive';

export type Business = {
  abn: string;
  abnStatus: AbnStatus;
  abnStatusEffectiveFrom: Nullable<string>;
  addressPostcode: Nullable<string>;
  addressState: Nullable<string>;
  asic: string;
  entityName: string;
  entityTypeName:
    | 'Australian Private Company'
    | 'Individual/Sole Trader'
    | string; // @IMPROVEMENT Complete this?
  entityTypeCode: string;
  gst: boolean;
  gstRegisteredFrom: string;
};

export type ApplicationDisplayData = {
  entityDisplayName?: string;
  entityShortId?: string;
  entityAllowCopy?: boolean;
  statusDisplayName?: string;
  workflowDisplayName?: string;
};

export type Hook = {
  id: string;
  key: string;
  status: 'allow' | 'deny';
  messages: string[];
};
export type SignatureService = {
  name: string;
  identifier: string;
  supports: Record<string, boolean>;
};

export type Feature = {
  enabled: boolean;
  key: string;
  name: string;
  options: Entity;
};

export type DealModel = AnyDocument & {
  title: string;
  brokerPriority: string;
  type?: string;
  applicants?: AnyDocument[];
};

export type DealConfig = {
  definition: string; // Document definition name
  statusDefinition: string; // Status definition name
  title: string;
  subtitle?: string;
  highlights: string[];
  workflow: string; // Workflow definition name
  documentationWorkflow?: string; // Documentation workflow definition name
  rework?: {
    statuses?: string[]; // Allowed statuses for rework; if omitted, all statuses are allowed
    transitions: string[]; // Allowed transitions for rework
  };
  manageContracts?: {
    workflow: string; // Manage contracts workflow definition name
    templateIdentifier: string; // Identifier for the contract template to use for this deal type
  };
  broker: {
    remunerationSelectorName: string;
  };
  individuals?: {
    title: string;
    selectorName: string;
  };
  withdrawal?: {
    reasonAttributeKey: string;
  };
  statuses: {
    quoteUnlockedStatusGroupKey: string;
  };
  list?: DealsListConfig;
  feature?: string;
  /** Required permission key(s) to access this deal type; evaluated via useCan. Omit to allow every broker. */
  permission?: string | readonly string[];
  defaultFilters?: Record<string, string>;
  quoteCalculator?: QuoteCalculatorConfig;
};
